import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import API_URL from '../../config/api';

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@400;500;600;700;800&display=swap');

.tai-wrap {
  min-height: 100vh;
  background: linear-gradient(160deg, #064e3b 0%, #0d9488 40%, #0369a1 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-family: 'Bricolage Grotesque', system-ui, sans-serif;
  padding: 24px 16px;
  position: relative;
  overflow: hidden;
}
.tai-wrap::before {
  content: '';
  position: absolute;
  top: -20%; right: -10%;
  width: 500px; height: 500px;
  background: radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%);
  border-radius: 50%;
}
.tai-wrap::after {
  content: '';
  position: absolute;
  bottom: -15%; left: -8%;
  width: 400px; height: 400px;
  background: radial-gradient(circle, rgba(3,105,161,0.25) 0%, transparent 70%);
  border-radius: 50%;
}
.tai-card {
  background: #fff;
  border-radius: 24px;
  padding: 44px 40px;
  max-width: 440px;
  width: 100%;
  box-shadow:
    0 20px 0 rgba(6,78,59,0.2),
    0 32px 64px rgba(0,0,0,0.22),
    inset 0 1px 0 rgba(255,255,255,0.9);
  position: relative;
  z-index: 1;
}
.tai-steps {
  display: flex; align-items: center; gap: 0;
  margin-bottom: 32px; justify-content: center;
}
.tai-step {
  display: flex; flex-direction: column; align-items: center; gap: 5px;
}
.tai-step-dot {
  width: 32px; height: 32px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 14px; font-weight: 800;
}
.tai-step-label { font-size: 10px; font-weight: 700; letter-spacing: .04em; color: #94a3b8; white-space: nowrap; }
.tai-step-line { width: 40px; height: 2px; background: #e2e8f0; margin: 0 4px; margin-bottom: 20px; }
.tai-icon-wrap {
  width: 68px; height: 68px;
  background: linear-gradient(135deg, #064e3b, #0d9488, #0369a1);
  border-radius: 18px;
  display: flex; align-items: center; justify-content: center;
  font-size: 32px;
  margin: 0 auto 16px;
  box-shadow: 0 8px 24px rgba(13,148,136,0.35);
}
.tai-title {
  margin: 0 0 4px;
  font-size: 22px; font-weight: 800; color: #0f172a;
  text-align: center; letter-spacing: -0.02em;
}
.tai-subtitle {
  margin: 0 0 28px;
  font-size: 14px; color: #64748b;
  text-align: center; font-weight: 500; line-height: 1.4;
}
.tai-label {
  display: block; font-size: 13px; font-weight: 700;
  color: #374151; margin-bottom: 6px;
}
.tai-input {
  width: 100%; padding: 12px 14px;
  border: 1.5px solid #d1d5db; border-radius: 10px;
  font-size: 15px; outline: none;
  font-family: inherit; box-sizing: border-box;
  transition: border-color 0.15s; color: #0f172a;
}
.tai-input:focus { border-color: #0d9488; box-shadow: 0 0 0 3px rgba(13,148,136,0.1); }
.tai-strength {
  margin-top: 6px; height: 4px; border-radius: 2px;
  background: #e2e8f0; overflow: hidden;
}
.tai-strength-bar { height: 100%; border-radius: 2px; transition: width .3s, background .3s; }
.tai-btn {
  width: 100%;
  background: linear-gradient(135deg, #0d9488, #0891b2);
  color: #fff; border: none; border-radius: 11px;
  padding: 14px; font-size: 15px; font-weight: 800;
  cursor: pointer; font-family: inherit;
  box-shadow: 0 4px 0 rgba(6,78,59,0.3), 0 8px 20px rgba(13,148,136,0.35);
  transition: opacity 0.15s, transform 0.12s;
  letter-spacing: -0.01em;
}
.tai-btn:hover:not(:disabled) { opacity: 0.92; transform: translateY(-1px); }
.tai-btn:active:not(:disabled) { transform: translateY(1px); box-shadow: 0 2px 0 rgba(6,78,59,0.3); }
.tai-btn:disabled { background: #9ca3af; cursor: not-allowed; box-shadow: none; }
.tai-error {
  background: #fef2f2; border: 1.5px solid #fca5a5;
  border-radius: 9px; padding: 10px 14px;
  margin-bottom: 20px; font-size: 13px; color: #dc2626;
  display: flex; align-items: center; gap: 8px;
}
.tai-hint {
  background: #f0fdfa; border-radius: 10px; padding: 12px 14px;
  margin-bottom: 20px; font-size: 13px; color: #065f46;
  line-height: 1.5;
}
.tai-footer {
  position: relative; z-index: 1;
  margin-top: 20px; font-size: 12px;
  color: rgba(255,255,255,0.7); text-align: center;
}
.tai-footer a { color: #a7f3d0; text-decoration: none; font-weight: 600; }
.tai-footer a:hover { color: #fff; }
`;

function pwStrength(pw: string): { pct: number; color: string; label: string } {
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 1) return { pct: 20, color: '#ef4444', label: 'Weak' };
  if (score <= 2) return { pct: 45, color: '#f97316', label: 'Fair' };
  if (score <= 3) return { pct: 65, color: '#eab308', label: 'Good' };
  if (score <= 4) return { pct: 82, color: '#22c55e', label: 'Strong' };
  return { pct: 100, color: '#10b981', label: 'Excellent' };
}

const TenantAcceptInvite: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const inviteToken = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!inviteToken) setError('Invalid invite link. Please ask your landlord to resend the invitation.');
  }, [inviteToken]);

  const strength = pwStrength(password);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError('Passwords do not match'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/tenant-portal/accept-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: inviteToken, password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to set up account'); return; }
      localStorage.setItem('tenantToken', data.token);
      localStorage.setItem('tenantInfo', JSON.stringify(data.tenant));
      setDone(true);
      setTimeout(() => navigate('/tenant/portal'), 1500);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tai-wrap">
      <style>{CSS}</style>

      <div className="tai-card">
        {/* Progress steps */}
        <div className="tai-steps">
          {[
            { icon: '✉️', label: 'Invited', done: true },
            { icon: '🔑', label: 'Set Password', done: false, active: !done },
            { icon: '🏠', label: 'Portal', done: done },
          ].map((s, i) => (
            <React.Fragment key={i}>
              {i > 0 && <div className="tai-step-line" style={{ background: s.done || i <= 1 ? '#0d9488' : '#e2e8f0' }} />}
              <div className="tai-step">
                <div className="tai-step-dot" style={{
                  background: s.done ? '#0d9488' : s.active ? '#fff' : '#f1f5f9',
                  border: s.active ? '2px solid #0d9488' : 'none',
                  color: s.done ? '#fff' : s.active ? '#0d9488' : '#94a3b8',
                }}>
                  {s.done && !s.active ? '✓' : s.icon}
                </div>
                <div className="tai-step-label" style={{ color: s.active ? '#0d9488' : s.done ? '#374151' : '#94a3b8' }}>
                  {s.label}
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>

        <div className="tai-icon-wrap">{done ? '✅' : '🔑'}</div>
        <h1 className="tai-title">{done ? 'Account Ready!' : 'Set Up Your Account'}</h1>
        <p className="tai-subtitle">
          {done
            ? 'Redirecting you to your Tenant Portal…'
            : 'Create a secure password to access your Tenant Portal'}
        </p>

        {!done && (
          <>
            <div className="tai-hint">
              🏠 You've been invited to manage your tenancy online. Set a strong password to get started.
            </div>

            {error && (
              <div className="tai-error">
                <span>⚠️</span> {error}
              </div>
            )}

            {inviteToken ? (
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: 18 }}>
                  <label className="tai-label">New password</label>
                  <input
                    className="tai-input"
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    minLength={8}
                    placeholder="At least 8 characters"
                    autoComplete="new-password"
                  />
                  {password && (
                    <div style={{ marginTop: 6 }}>
                      <div className="tai-strength">
                        <div className="tai-strength-bar" style={{ width: `${strength.pct}%`, background: strength.color }} />
                      </div>
                      <div style={{ fontSize: 11, color: strength.color, fontWeight: 700, marginTop: 3 }}>
                        {strength.label} password
                      </div>
                    </div>
                  )}
                </div>
                <div style={{ marginBottom: 26 }}>
                  <label className="tai-label">Confirm password</label>
                  <input
                    className="tai-input"
                    type="password"
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    required
                    placeholder="••••••••"
                    autoComplete="new-password"
                    style={{ borderColor: confirm && confirm !== password ? '#fca5a5' : undefined }}
                  />
                  {confirm && confirm !== password && (
                    <div style={{ fontSize: 12, color: '#dc2626', marginTop: 4, fontWeight: 600 }}>Passwords don't match</div>
                  )}
                </div>
                <button type="submit" className="tai-btn" disabled={loading || !inviteToken}>
                  {loading ? 'Creating account…' : 'Create Account & Sign In →'}
                </button>
              </form>
            ) : (
              <div style={{ textAlign: 'center' }}>
                <Link to="/tenant/login" style={{ color: '#0d9488', fontWeight: 700 }}>Already have an account? Sign in →</Link>
              </div>
            )}
          </>
        )}
      </div>

      <p className="tai-footer">
        Already have an account?{' '}
        <Link to="/tenant/login">Sign in here</Link>
      </p>
    </div>
  );
};

export default TenantAcceptInvite;
